import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { Dialog } from "primereact/dialog";
import { Button } from "@material-ui/core";
import { Driver } from "../../types/driver.types";
import { validationSchema } from "./validation";
import { cleanDriverData } from "./utils";
import { DriverFormProps } from "./types";
import { LicenseSection } from "./LicenseSection";
import { PersonalInfoSection } from "./PersonalInfoSection";
import { ContactSection } from "./ContactSection";
import { AccountStatusSection } from "./AccountStatusSection";
import { ChaptersSection } from "./ChaptersSection";
import { configureFrenchLocale } from "./locale";
import { getInitialValues } from "./initialValues";

export const DriverForm: React.FC<DriverFormProps> = ({
  visible,
  onHide,
  onSave,
  driver,
  isTrial,
}) => {
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    configureFrenchLocale();
  }, []);

  const handleSubmit = async (
    values: Omit<Driver, "_id" | "password">,
    {
      setSubmitting,
      resetForm,
    }: { setSubmitting: (isSubmitting: boolean) => void; resetForm: () => void }
  ) => {
    const cleanedValues = cleanDriverData({
      ...values,
      creationDate: new Date(),
    });
    try {
      setServerError(null);
      const result = await onSave(cleanedValues);
      if (result.success) {
        resetForm();
        onHide();
      } else {
        setServerError(
          result.error || "Failed to save driver. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setServerError(
        error.message ||
          "Une erreur s'est produite lors de l'enregistrement du chauffeur. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "550px" }}
      header="Ajouter un chauffeur"
      modal
      className="p-fluid rounded-2xl overflow-clip mx-2 sm-mx-0 font-sans"
      onHide={() => {
        setServerError(null);
        onHide();
      }}
    >
      <Formik
        initialValues={getInitialValues(driver)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, submitForm }) => (
          <Form
            id="driverForm"
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <LicenseSection setFieldValue={setFieldValue} />
            <PersonalInfoSection setFieldValue={setFieldValue} />
            <ContactSection isTrial={isTrial} driver={driver} />
            <ChaptersSection transporterId={values.transporter} />
            <AccountStatusSection
              values={values}
              setFieldValue={setFieldValue}
              isTrial={isTrial}
            />

            {serverError && (
              <div className="text-red-500 text-sm mb-4">{serverError}</div>
            )}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                className="p-button-text ring-1 ring-primary text-primary rounded-xl !font-sans capitalize w-fit"
                onClick={() => {
                  setServerError(null);
                  onHide();
                }}
                type="button"
              >
                Annuler
              </Button>
              <Button
                startIcon={<i className="pi pi-check"></i>}
                className="p-button-text ring-1 ring-primary bg-primary text-white rounded-xl capitalize !font-sans w-fit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export * from "./types";
